import {
  FormControl,
  FormHelperText,
  MenuItem,
  RadioGroup,
  Select,
  Switch,
  TextField,
  styled,
  type FormControlProps,
  type RadioGroupProps,
  type SelectProps,
  type SwitchProps,
  type TextFieldProps,
} from '@mui/material';
import { useSettingIds } from '~/components/settings';
import { useUniqueId } from '~/hooks/unique-id';
import { PasswordField, type PasswordFieldProps } from './password-field';

/**
 * Names a text field by the enclosing settings row's `SettingName` and describes it by the
 * row's `SettingDescription` plus its own helper text. Outside a row the props pass through.
 */
const useDetailFieldProps = <T extends TextFieldProps>(props: T): T => {
  const ids = useSettingIds();
  if (!ids) return props;
  const id = props.id ?? ids.inputId;
  // Setting inputProps replaces MUI's own aria-describedby, which points at the helper text.
  const describedBy =
    [
      ids.descriptionId,
      props.helperText ? `${id}-helper-text` : null,
      props.inputProps?.['aria-describedby'],
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  // aria-labelledby as well as the label's htmlFor, so the row names the field whatever its id.
  // A `select` field renders MUI's Select, whose combobox is named through labelId instead.
  return {
    ...props,
    id,
    inputProps: {
      ...props.inputProps,
      'aria-labelledby': ids.labelId,
      'aria-describedby': describedBy,
    },
    ...(props.select && { SelectProps: { labelId: ids.labelId, ...props.SelectProps } }),
  };
};

// A minimum rather than a fixed height, so helper and error text below the input is not clipped.
const DetailTextFieldRoot = styled(TextField)({
  flexBasis: '45%',
  minHeight: '3rem',
  margin: 0,
});

export const DetailTextField = (props: TextFieldProps) => (
  <DetailTextFieldRoot
    fullWidth
    margin="dense"
    variant="outlined"
    {...useDetailFieldProps(props)}
  />
);

const DetailPasswordFieldRoot = styled(PasswordField)({
  flexBasis: '45%',
  minHeight: '3rem',
  margin: 0,
});

export const DetailPasswordField = (props: PasswordFieldProps) => (
  <DetailPasswordFieldRoot {...useDetailFieldProps(props)} />
);

const DetailTextAreaRoot = styled(DetailTextFieldRoot)({
  flexBasis: '60%',
  minHeight: 'initial',
});

export const DetailTextArea = (props: TextFieldProps) => (
  <DetailTextAreaRoot
    fullWidth
    margin="dense"
    variant="outlined"
    multiline
    minRows={4}
    {...useDetailFieldProps(props)}
  />
);

const DetailSelectControl = styled(FormControl)(() => ({
  flexBasis: '45%',
  minHeight: '3rem',
  display: 'block',
}));

export const DetailSelect = ({
  children,
  error,
  formControlProps,
  helperText,
  placeholder,
  ...props
}: SelectProps & { helperText?: string; formControlProps?: FormControlProps }) => {
  const ids = useSettingIds();
  const fallbackId = useUniqueId('detail-select');
  const id = props.id ?? ids?.inputId ?? fallbackId;
  // Described by the row's description and its own helper text, like DetailTextField.
  const helperTextId = helperText ? `${id}-helper-text` : undefined;
  const describedBy = [ids?.descriptionId, helperTextId].filter(Boolean).join(' ') || undefined;
  return (
    <DetailSelectControl error={error} {...formControlProps}>
      <Select
        fullWidth
        id={id}
        labelId={ids?.labelId}
        margin="dense"
        {...props}
        inputProps={{ 'aria-describedby': describedBy, ...props.inputProps }}
      >
        {placeholder && <MenuItem disabled>{placeholder}</MenuItem>}
        {children}
      </Select>
      <FormHelperText id={helperTextId}>{helperText}</FormHelperText>
    </DetailSelectControl>
  );
};

export const DetailSwitch = (props: SwitchProps) => {
  const ids = useSettingIds();
  return (
    <Switch
      id={ids?.inputId}
      {...props}
      inputProps={{ 'aria-describedby': ids?.descriptionId, ...props.inputProps }}
    />
  );
};

export const DetailRadioGroup = (props: RadioGroupProps) => {
  const ids = useSettingIds();
  return (
    <RadioGroup aria-labelledby={ids?.labelId} aria-describedby={ids?.descriptionId} {...props} />
  );
};
